import React, { createContext, useContext, useEffect, useState } from 'react';

import { useAuth } from 'contexts/auth';
import { useUser2 } from 'contexts/user2';
import { useWebsocket } from 'contexts/websocket';
import courseService from 'services/course';

const ChatContext = createContext();
const useChat = () => useContext(ChatContext);

const ChatProvider = (props) => {
    const { auth } = useAuth();
    const { groups } = useUser2();
    const { sendOn, sendCustomWSOn, messageWS } = useWebsocket();

    const [messagesToProcess, setMessagesToProcess] = useState([]);
    const [data, setData] = useState([]);

    const sendMessage = (payload) => {
        sendOn('default', {
            payload_type: 'send',
            payload,
        });
    };

    const unsendMessage = (message) => {
        sendOn('default', {
            payload_type: 'delete',
            payload: { id: message.id, channel_id: message.channel_id },
        });
    };

    const readChannel = (channelId, messageId) => {
        setData((current) =>
            current.map((group) => {
                const channel = group.result.find(({ id }) => id === channelId);
                if (!channel) return group;
                const newResult = group.result.map((result) => ({
                    ...result,
                    unread: result.id === channelId ? false : result.unread,
                }));
                const unreadChannelsCount = newResult
                    .filter(({ chat_type }) => chat_type === 'channel')
                    .reduce((acc, cur) => {
                        if (cur.unread) return acc + 1;
                        return acc;
                    }, 0);
                const unreadDirectsCount = newResult
                    .filter(({ chat_type }) => chat_type === 'direct')
                    .reduce((acc, cur) => {
                        if (cur.unread) return acc + 1;
                        return acc;
                    }, 0);
                return {
                    ...group,
                    result: newResult,
                    unread_channels: unreadChannelsCount,
                    unread_directs: unreadDirectsCount,
                };
            })
        );
        sendCustomWSOn('read', {
            payload_type: 'read',
            payload: { channel_id: channelId, message_id: messageId, upserted_on: new Date().getTime() },
        });
    };

    const callOnMessage = (messageWS) => {
        const { normalMessage } = messageWS;
        if (!normalMessage) return;
        if (normalMessage.data.is_sender) return;
        setMessagesToProcess((current) => [...current, normalMessage.data]);
        if (normalMessage.data.payload_type === 'send') {
            const message = normalMessage.data.payload;
            const group = data.find(({ group_id }) => group_id === message.group_id);
            const channel = group.result.find(({ id }) => id === message.channel_id);
            if (!channel) {
                (async () => {
                    const response = await courseService.listChannels(auth.course_id);
                    setData(response);
                })();
            } else {
                setData((current) => {
                    return current.map((group) => {
                        if (group.group_id !== message.group_id) return group;
                        const newResult = group.result.map((result) => ({
                            ...result,
                            unread: result.id === message.channel_id ? true : result.unread,
                        }));
                        const unreadChannelsCount = newResult
                            .filter(({ chat_type }) => chat_type === 'channel')
                            .reduce((acc, cur) => {
                                if (cur.unread) return acc + 1;
                                return acc;
                            }, 0);
                        const unreadDirectsCount = newResult
                            .filter(({ chat_type }) => chat_type === 'direct')
                            .reduce((acc, cur) => {
                                if (cur.unread) return acc + 1;
                                return acc;
                            }, 0);
                        return {
                            ...group,
                            result: newResult,
                            unread_channels: unreadChannelsCount,
                            unread_directs: unreadDirectsCount,
                        };
                    });
                });
            }
        }
    };

    useEffect(() => {
        callOnMessage(messageWS);
    }, [messageWS]);

    useEffect(() => {
        (async () => {
            const response = await courseService.listChannels(auth.course_id);
            setData(response);
        })();
    }, [auth, groups]);

    const value = {
        messagesToProcess,
        setMessagesToProcess,
        data,
        setData,
        sendMessage,
        unsendMessage,
        readChannel,
    };

    return <ChatContext.Provider value={value} {...props} />;
};

export { useChat };
export default ChatProvider;
